import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import MiniLampViewer from '../components/MiniLampViewer';
import { getItemName, getItemSpecs, getModelCode, getItemPrice, getItemRenderInfo } from '../utils/cartHelpers';
import './Cart.css';

export default function Cart() {
  const { items, loading, error, removeFromCart, updateQuantity, getTotalPrice } = useCart();
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();

  const handleCheckout = () => {
    if (!isAuthenticated) {
      navigate('/login', { state: { from: { pathname: '/cart' } } });
      return;
    }
    navigate('/checkout');
  };

  // Show loading state
  if (loading && items.length === 0) {
    return (
      <div className="cart-page">
        <div className="cart-loading">
          <div className="loading-spinner"></div>
          <p>Loading your cart...</p>
        </div>
      </div>
    );
  }

  // Show empty cart
  if (items.length === 0) {
    return (
      <div className="cart-page">
        <div className="cart-empty">
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.5 }}
          >
            <div className="empty-icon">🛒</div>
            <h2>Your cart is empty</h2>
            <p>Start designing your perfect lamp to add items to your cart</p>
            <Link to="/design" className="cta-button primary">
              Start Designing
            </Link>
          </motion.div>
        </div>
      </div>
    );
  }

  return (
    <div className="cart-page">
      <div className="cart-container">
        <motion.h1
          className="cart-title"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          Shopping Cart
          {loading && <span className="cart-syncing">Syncing...</span>}
        </motion.h1>

        {error && (
          <div className="cart-error">
            <span>⚠️ {error}</span>
          </div>
        )}

        <div className="cart-content">
          <div className="cart-items">
            <AnimatePresence>
              {items.map((item, index) => {
                const specs = getItemSpecs(item);
                const renderInfo = getItemRenderInfo(item);
                return (
                  <motion.div
                    key={item.id}
                    className="cart-item"
                    initial={{ opacity: 0, x: -20 }}
                    animate={{ opacity: 1, x: 0 }}
                    exit={{ opacity: 0, x: 20 }}
                    transition={{ delay: index * 0.1 }}
                  >
                    <div className="cart-item-image">
                      {renderInfo.type === '3d' ? (
                        <MiniLampViewer
                          baseNumber={renderInfo.baseNumber}
                          shadeNumber={renderInfo.shadeNumber}
                          baseColor={renderInfo.baseColor}
                          shadeColor={renderInfo.shadeColor}
                        />
                      ) : renderInfo.type === 'swatch' ? (
                        <div className="cart-item-swatch-preview">
                          <div className="swatch-lamp-icon">
                            <div className="swatch-shade" style={{ backgroundColor: renderInfo.shadeColor }}></div>
                            <div className="swatch-base" style={{ backgroundColor: renderInfo.baseColor }}></div>
                          </div>
                        </div>
                      ) : (
                        <img
                          src={renderInfo.src}
                          alt={getItemName(item)}
                        />
                      )}
                    </div>

                    <div className="cart-item-details">
                      <h3 className="cart-item-title">
                        {getItemName(item)}
                      </h3>
                      <div className="cart-item-specs">
                        {specs.baseColor && (
                          <span className="spec">
                            Base: <strong>{specs.baseColor}</strong>
                          </span>
                        )}
                        {specs.shadeColor && (
                          <span className="spec">
                            Shade: <strong>{specs.shadeColor}</strong>
                          </span>
                        )}
                      </div>
                      {getModelCode(item) && (
                        <div className="cart-item-code">
                          Code: {getModelCode(item)}
                        </div>
                      )}
                      <div className="cart-item-price">
                        ₹{getItemPrice(item).toLocaleString()}
                        {item.priceChanged && item.currentPrice && (
                          <span className="price-changed">
                            (now ₹{parseFloat(item.currentPrice).toLocaleString()})
                          </span>
                        )}
                      </div>
                    </div>

                    <div className="cart-item-controls">
                      <div className="quantity-controls">
                        <button
                          className="quantity-btn"
                          onClick={() => updateQuantity(item.id, item.quantity - 1)}
                          disabled={loading}
                        >
                          −
                        </button>
                        <span className="quantity-value">{item.quantity}</span>
                        <button
                          className="quantity-btn"
                          onClick={() => updateQuantity(item.id, item.quantity + 1)}
                          disabled={loading}
                        >
                          +
                        </button>
                      </div>
                      <button
                        className="remove-btn"
                        onClick={() => removeFromCart(item.id)}
                        disabled={loading}
                      >
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                          <polyline points="3 6 5 6 21 6" />
                          <path d="M19 6v14a2 2 0 0 1-2 2H7a2 2 0 0 1-2-2V6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2" />
                        </svg>
                      </button>
                    </div>
                  </motion.div>
                );
              })}
            </AnimatePresence>
          </div>

          <motion.div
            className="cart-summary"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
          >
            <h2 className="summary-title">Order Summary</h2>
            <div className="summary-row">
              <span>Subtotal ({items.reduce((sum, i) => sum + i.quantity, 0)} items)</span>
              <span>₹{getTotalPrice().toLocaleString()}</span>
            </div>
            <div className="summary-row">
              <span>Shipping</span>
              <span>Calculated at checkout</span>
            </div>
            <div className="summary-divider"></div>
            <div className="summary-row total">
              <span>Total</span>
              <span>₹{getTotalPrice().toLocaleString()}</span>
            </div>
            <motion.button
              className="checkout-button"
              onClick={handleCheckout}
              whileHover={{ scale: 1.02 }}
              whileTap={{ scale: 0.98 }}
              disabled={loading}
            >
              {loading ? 'Processing...' : 'Proceed to Checkout'}
            </motion.button>
            <Link to="/design" className="continue-shopping">
              Continue Shopping
            </Link>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
